import { IconProps } from '@tamagui/helpers-icon';
import {
  ChartLine,
  ChevronDown,
  Divide,
  DollarSign,
  HandCoins,
  Percent,
  PieChart,
  Search,
  Share2,
  TrendingUp
} from '@tamagui/lucide-icons';
import { Accordion } from '@unistocks/components/accordion/accordion';
import { Image } from '@unistocks/components/basic/image';
import { Checkbox } from '@unistocks/components/checkbox/checkbox';
import { HelpText } from '@unistocks/components/form/components/help-text';
import { WatchValues } from '@unistocks/components/form/components/watch-values';
import { Form } from '@unistocks/components/form/form';
import { MultiStepForm } from '@unistocks/components/form/multi-step-form';
import { currencyTransform } from '@unistocks/components/form/validation-utils';
import { applyRefinements, oneOf, requiredSchema } from '@unistocks/components/form/validators';
import { Card } from '@unistocks/components/layout/card';
import { Column, Row } from '@unistocks/components/layout/flex';
import { UnistocksCircle } from '@unistocks/components/logo/unistocks-circle';
import { HeaderIcon } from '@unistocks/components/modal/header-icon';
import { H4, Heading, Paragraph } from '@unistocks/components/text/text';
import { AssetTypeSelect } from '@unistocks/features/assets/components/asset-type-select';
import { useAssetSearch } from '@unistocks/features/assets/hooks/use-asset-search';
import { getCombinedExampleValue } from '@unistocks/features/dashboard/components/calculated-value/examples';
import { combinationFlag } from '@unistocks/features/dashboard/components/calculated-value/value-cells';
import {
  getDashboardValueSources,
  getDashboardValueTypes,
  getDefaultNestedValue,
  getEmptyCalculatedValue,
  getKeyFinancialRatioSelectOptions,
  getNetSavingsTypeSelectOptions,
  sourceHasType
} from '@unistocks/features/dashboard/components/dashboard-data/event-view/utils';
import { DashboardItemFrame } from '@unistocks/features/dashboard/components/dashboard-item/dashboard-item';
import { Header } from '@unistocks/features/dashboard/components/dashboard-item/dashboard-item-header';
import { PeriodSelect } from '@unistocks/features/dashboard/components/settings/period-select';
import { PortfolioFilter } from '@unistocks/features/dashboard/components/settings/portfolio-filter';
import { ValueListRow } from '@unistocks/features/dashboard/components/value-list-item/value-list-row';
import { ValueListTable } from '@unistocks/features/dashboard/components/value-list-table/value-list-table';
import {
  keyFinancialRatioOptions,
  netSavingsTypeOptions,
  valueSourceOptions
} from '@unistocks/features/dashboard/utils';
import { FlagKey, FLAGS } from '@unistocks/features/portfolios/flags';
import { selectSelectedPortfolioId } from '@unistocks/features/portfolios/store/portfolio.slice';
import {
  AssetType,
  CalculatedValueDto,
  DateConstant,
  ExternalAssetSearchDto,
  KeyFinancialRatio,
  NetSavingsType,
  ValueSource,
  ValueType
} from '@unistocks/proxy/axios-client';
import { usePortfoliosGetListQuery } from '@unistocks/proxy/axios-client/PortfoliosQuery';
import { useSharedPortfoliosGetListQuery } from '@unistocks/proxy/axios-client/SharedPortfoliosQuery';
import { useAppSelector } from '@unistocks/shared/hooks/redux-hooks';
import { useCurrencyList } from '@unistocks/shared/hooks/use-currency-list';
import { t } from '@unistocks/shared/localization/index.ts';
import {
  currencyTypeaheadFilter,
  currencyValueFormatter
} from '@unistocks/shared/utils/currency-utils';
import { assetTypeOptions } from '@unistocks/shared/utils/enums/asset-type-options';
import { extractSymbol } from '@unistocks/shared/utils/list-utils';
import {
  combineFlags,
  hasAnyFlag,
  hasFlag,
  isDefined,
  isNumber,
  toggleFlag
} from '@unistocks/shared/utils/util';
import { FormProps } from '@unistocks/types/form-props';
import React, { useRef, useState } from 'react';
import { UseFormGetValues, UseFormReturn, useWatch } from 'react-hook-form';
import { AnimatePresence, Separator, Square } from 'tamagui';
import { z } from 'zod';

// Value types that depend on a date period
const periodDependentFlags = combineFlags([
  ValueType.NominalChange,
  ValueType.PercentageChange,
  ValueType.NetSavings
]);

const requiredMessage = (labelKey: string) => t('::The {0} field is required.', { 0: t(labelKey) });
const selectionMessage = (labelKey: string) =>
  t('::ValidationError:Required:Selection', { 0: t(labelKey) });

const schemaFactory = (depth = 0): z.ZodType =>
  applyRefinements(
    z.object({
      displayName: z.string().nullish(),
      valueType: requiredSchema(
        z.number().refine(
          value => hasFlag(ValueType.All, value),
          t('::ValidationError:Required:Selection', {
            0: t('::Dashboard:Settings:EventView:ValueType')
          })
        ),
        t('::Dashboard:Settings:EventView:ValueType')
      ),
      valueSource: oneOf(valueSourceOptions.map(({ value }) => value)).nullish(),
      params: z
        .object({
          period: z.number().nullish(),
          fromCurrency: z
            .object({
              code: z.string(),
              displayName: z.string()
            })
            .nullish(),
          toCurrency: z
            .object({
              code: z.string(),
              displayName: z.string()
            })
            .nullish(),
          netSavingsType: oneOf(netSavingsTypeOptions.map(({ value }) => value)).nullish(),
          keyFinancialRatio: oneOf(keyFinancialRatioOptions.map(({ value }) => value)).nullish(),
          portfolios: z.array(z.string()).nullish(),
          sharedPortfolios: z.array(z.string()).nullish(),
          sections: z.array(z.string()).nullish(),
          assetTypes: z.array(oneOf(assetTypeOptions.map(({ value }) => value))).nullish(),
          assets: z.array(z.string()).nullish(),
          assetSearch: z
            .looseObject({
              dataSource: oneOf(assetTypeOptions.map(({ value }) => value)),
              identifier: z.string()
            })
            .nullish(),
          assetCollection: z.number().nullish()
        })
        .nullish(),
      nestedValues:
        depth === 0
          ? z.array(schemaFactory(depth + 1)).nullish()
          : z.array(z.any()).max(0).nullish()
    }),
    (values, ctx) => {
      const { displayName, valueType, valueSource, params, nestedValues } = values;
      const isRelative = valueType === ValueType.RelativeValue;
      const isPortfolioSource = valueSource === ValueSource.Portfolio;
      const isSharedPortfolioSource = valueSource === ValueSource.SharedPortfolio;

      const addIssue = (path: (string | number)[], message: string) =>
        ctx.addIssue({ code: 'custom', path, message });

      if (depth === 0 && !isDefined(displayName)) {
        addIssue(['displayName'], requiredMessage('::Dashboard:Settings:EventView:Description'));
      }

      if (!isRelative && !isDefined(valueSource)) {
        addIssue(['valueSource'], requiredMessage('::Dashboard:Settings:EventView:ValueSource'));
      }

      if (valueType === ValueType.NetSavings && !isNumber(params?.netSavingsType)) {
        addIssue(['params', 'netSavingsType'], selectionMessage('::NetSavingsType'));
      }

      if (valueType === ValueType.KeyFinancialRatio) {
        if (!isNumber(params?.keyFinancialRatio)) {
          addIssue(['params', 'keyFinancialRatio'], selectionMessage('::KeyFinancialRatio'));
        }

        if (!params?.portfolios || params.portfolios.length !== 1) {
          addIssue(['params', 'portfolios'], selectionMessage('::Portfolios'));
        }
      } else {
        const atLeastOneRequired =
          depth === 0 ? isPortfolioSource && !isRelative : isPortfolioSource;

        if (atLeastOneRequired && !params?.portfolios?.length) {
          addIssue(['params', 'portfolios'], selectionMessage('::Portfolios'));
        }
      }

      const sharedRequired =
        depth === 0 ? isSharedPortfolioSource && !isRelative : isSharedPortfolioSource;

      if (sharedRequired && params?.sharedPortfolios?.length !== 1) {
        addIssue(['params', 'sharedPortfolios'], selectionMessage('::Portfolios'));
      }

      if (valueSource === ValueSource.Asset) {
        const assetSearch = params?.assetSearch;

        if (!assetSearch?.identifier || !isNumber(assetSearch.dataSource)) {
          addIssue(['params', 'assetSearch'], selectionMessage('::Asset'));
        }
      }

      if (depth === 0 && isRelative && nestedValues?.length !== 2) {
        addIssue(['nestedValues'], selectionMessage('::Dashboard:Settings:EventView:Events'));
      }
    }
  );

const validAssetTypes = [
  AssetType.Stock,
  AssetType.Fund,
  AssetType.CryptoCurrency,
  AssetType.Index,
  AssetType.Commodity,
  AssetType.Other
];

const formId = 'event-settings-form';

export type EventSettingsFormProps = FormProps<CalculatedValueDto> & {
  isEdit?: boolean;
};

export const EventSettingsForm: React.FC<EventSettingsFormProps> = ({ isEdit, ...props }) => {
  const schema = schemaFactory();
  const formRef = useRef<UseFormReturn<CalculatedValueDto>>(null);
  const defaultPortfolio = useAppSelector(selectSelectedPortfolioId);
  const currencies = useCurrencyList();
  const [selectedAssetType, setSelectedAssetType] = useState<AssetType>(
    props.initialValues?.params?.assetSearch?.assetType ?? AssetType.Stock
  );
  const { search } = useAssetSearch([selectedAssetType]);

  const { data: portfolios, isSuccess: isSuccessPortfolios } = usePortfoliosGetListQuery();
  const { data: sharedPortfolios, isSuccess: isSuccessSharedPortfolios } =
    useSharedPortfoliosGetListQuery(0, 100);

  const valueSourceOptions = getDashboardValueSources();

  /**
   * Generate a display name based on the value source, type and other parameters
   */
  const getDefaultDisplayName = (
    source: ValueSource,
    type: ValueType,
    getValues: UseFormGetValues<CalculatedValueDto>
  ): string => {
    let displayName = '',
      portfolioName = '';
    const period = getValues('params.period');
    const periodName = isNumber(period) ? t(`::Enum:DateConstant:${period}`) : '';

    switch (source) {
      case ValueSource.Portfolio:
        switch (type) {
          case ValueType.KeyFinancialRatio:
            const keyFinancialRatio = getValues('params.keyFinancialRatio');
            displayName = isDefined(keyFinancialRatio)
              ? t(`::Enum:KeyFinancialRatio:${keyFinancialRatio}`)
              : '';
            break;

          case ValueType.NetSavings:
            displayName = periodName ? `${t('::NetSavings')}, ${periodName}` : '';
            break;
        }

        if (!displayName) {
          const selectedPortfolios = getValues('params.portfolios') || [];
          portfolioName =
            isSuccessPortfolios && selectedPortfolios.length === 1
              ? (portfolios.items.find(p => p.id === selectedPortfolios[0])?.displayName ?? '')
              : '';

          displayName = portfolioName;
          if (hasAnyFlag(type, [ValueType.NominalChange, ValueType.PercentageChange]))
            displayName += `${portfolioName ? ', ' : ''}${periodName}`;
        }

        break;

      case ValueSource.SharedPortfolio:
        const selectedSharedPortfolios = getValues('params.sharedPortfolios') || [];

        portfolioName =
          isSuccessSharedPortfolios && selectedSharedPortfolios.length === 1
            ? (sharedPortfolios.items.find(p => p.id === selectedSharedPortfolios[0])
                ?.portfolioName ?? '')
            : '';
        displayName = portfolioName;

        if (periodName) displayName += `, ${periodName}`;
        break;

      case ValueSource.Asset:
        const asset = getValues('params.assetSearch');
        displayName = asset?.name ?? '';

        if (periodName) {
          displayName += `, ${periodName}`;
        }
    }

    return displayName;
  };

  /**
   * Set the default display name based on the values in the form, if the display name has not been edited by the user
   */
  const setDisplayName = () => {
    if (isEdit || !formRef.current) return;

    const [valueSource, valueType] = formRef.current.getValues(['valueSource', 'valueType']);
    formRef.current?.setValue(
      'displayName',
      getDefaultDisplayName(valueSource!, valueType, formRef.current.getValues)
    );
  };

  return (
    <MultiStepForm<CalculatedValueDto>
      ref={formRef}
      formId={formId}
      schema={schema}
      strict={!isDefined(props.initialValues?.valueSource)}
      triggerProps={{ submitButtonType: 'button' }}
      onStepChange={setDisplayName}
      {...props}
    >
      <MultiStepForm.Step<CalculatedValueDto>
        label={t('::Dashboard:Settings:EventView:ValueSource')}
      >
        {(form, submit) => (
          <Form.FieldContainer<CalculatedValueDto> name="valueSource">
            <WatchValues<CalculatedValueDto> names={['valueSource', 'valueType']}>
              {([valueSource, valueType]) => {
                const onValueSourceChange = (source: ValueSource) => {
                  if (source === valueSource) return submit();

                  let type = valueType ?? ValueType.TotalValue;

                  if (!sourceHasType(source, type)) {
                    type = ValueType.TotalValue;
                  }

                  const resetValues: CalculatedValueDto = {
                    ...getEmptyCalculatedValue(),
                    valueSource: source,
                    valueType: type,
                    displayName: getDefaultDisplayName(source, valueType, form.getValues)
                  };

                  switch (source) {
                    case ValueSource.Portfolio:
                      resetValues.params!.portfolios = defaultPortfolio ? [defaultPortfolio] : [];
                      break;

                    case ValueSource.SharedPortfolio:
                      break;

                    case ValueSource.Asset:
                      break;

                    case ValueSource.ExchangeRate:
                      break;
                  }

                  form.reset(resetValues);

                  return submit();
                };

                return (
                  <Column gap={'$3'} mb={'$3'}>
                    {valueSourceOptions.map(({ key, value: source }) => (
                      <Card
                        key={source}
                        pressable={'color'}
                        hoverable
                        bordered
                        bg={source === valueSource ? '$color4' : 'transparent'}
                        onPress={() => onValueSourceChange(source)}
                      >
                        <Card.Header>
                          <H4>{key}</H4>
                          {renderValueSourceIcon(source)}
                        </Card.Header>
                        <Card.Body>
                          <Paragraph userSelect="none">
                            {t(`::Dashboard:Settings:ValueSource:${source}:Description`)}
                          </Paragraph>
                        </Card.Body>
                      </Card>
                    ))}
                  </Column>
                );
              }}
            </WatchValues>
          </Form.FieldContainer>
        )}
      </MultiStepForm.Step>

      <MultiStepForm.Step<CalculatedValueDto> label={t('::Dashboard:Settings:EventView:ValueType')}>
        {form => (
          <WatchValues<CalculatedValueDto> names={['valueSource', 'valueType', 'displayName']}>
            {([valueSource, valueType, displayName]) => {
              const valueTypeOptions = getDashboardValueTypes(valueSource ?? undefined);

              const onValueTypeChange = (next: ValueType) => {
                // TODO: Could detect whether to reset or not. This impl. will not work when selecting the same value type as the initial value.
                let type: ValueType;

                if (hasFlag(combinationFlag, valueType) && hasFlag(combinationFlag, next)) {
                  type = toggleFlag(valueType, next);
                } else {
                  type = next === valueType ? (null as any) : next;
                }

                const values: CalculatedValueDto = {
                  ...getEmptyCalculatedValue(),
                  displayName,
                  valueSource,
                  valueType: type
                };

                form.reset(prev => {
                  const portfolios = defaultPortfolio ? [defaultPortfolio] : [];

                  if (valueSource === ValueSource.Portfolio)
                    values.params!.portfolios = prev.params?.portfolios ?? portfolios;

                  if (valueSource === ValueSource.SharedPortfolio)
                    values.params!.sharedPortfolios = prev.params?.sharedPortfolios ?? [];

                  if (hasAnyFlag(type, [ValueType.NominalChange, ValueType.PercentageChange]))
                    values.params!.period = prev.params?.period ?? DateConstant.ThisYear;

                  switch (type) {
                    case ValueType.NetSavings:
                      values.params!.portfolios = prev.params?.portfolios ?? portfolios;
                      values.params!.period = prev.params?.period ?? DateConstant.ThisYear;
                      values.params!.netSavingsType =
                        prev.params?.netSavingsType ?? NetSavingsType.PerMonth;
                      break;

                    case ValueType.KeyFinancialRatio:
                      values.params!.portfolios = prev.params?.portfolios ?? portfolios;
                      values.params!.keyFinancialRatio =
                        prev.params?.keyFinancialRatio ?? KeyFinancialRatio.CAGR;
                      break;

                    case ValueType.RelativeValue:
                      values.params = null;
                      values.nestedValues = [getDefaultNestedValue(), getDefaultNestedValue()];
                  }

                  return values;
                });
                // Set display name after reset to ensure it's set based on the updated form values
                if (!isEdit)
                  form.setValue(
                    'displayName',
                    getDefaultDisplayName(valueSource!, type, form.getValues)
                  );
              };

              return (
                <>
                  {valueTypeOptions.map(({ key, value }) => {
                    return (
                      <React.Fragment key={value}>
                        <Checkbox
                          label={key}
                          subTitle={t(`::Dashboard:Settings:ValueType:${value}:Description`)}
                          checked={hasFlag(valueType, value)}
                          onCheckedChange={() => onValueTypeChange(value)}
                          iconAfter={renderValueTypeIcon(value)}
                        />
                        <Separator />
                      </React.Fragment>
                    );
                  })}

                  <ExampleValueList />
                </>
              );
            }}
          </WatchValues>
        )}
      </MultiStepForm.Step>

      <MultiStepForm.Step<CalculatedValueDto> label={t('::Dashboard:Settings:EventView:Filter')}>
        <WatchValues<CalculatedValueDto> names={['valueType', 'valueSource', 'params.portfolios']}>
          {([valueType, valueSource, portfolios]) => {
            const renderValueFilterGroup = () => (
              <>
                {/* Portfolio Filter */}
                <PortfolioFilter
                  sharedPortfolios={valueSource === ValueSource.SharedPortfolio}
                  portfolioSelectOverrides={{
                    name:
                      valueSource === ValueSource.SharedPortfolio
                        ? 'params.sharedPortfolios'
                        : 'params.portfolios',
                    helpText: t('::Dashboard:Settings:PortfolioFilter:Portfolio:HelpText'),
                    multiple:
                      valueType !== ValueType.KeyFinancialRatio &&
                      valueType !== ValueType.NetSavings &&
                      valueSource !== ValueSource.SharedPortfolio,
                    hidden:
                      valueType === ValueType.RelativeValue ||
                      ![ValueSource.Portfolio, ValueSource.SharedPortfolio].includes(valueSource!),
                    noDataText:
                      valueSource === ValueSource.SharedPortfolio
                        ? t('::Portfolio:Share:NoSharedPortfolios')
                        : undefined,
                    noDataIcon: valueSource === ValueSource.SharedPortfolio ? PieChart : undefined,
                    onChange: setDisplayName
                  }}
                  sectionSelectOverrides={{
                    name: 'params.sections',
                    helpText: t('::Dashboard:Settings:PortfolioFilter:Category:HelpText'),
                    hidden:
                      valueType !== ValueType.TotalValue || valueSource !== ValueSource.Portfolio
                  }}
                  assetTypeSelectOverrides={{
                    name: 'params.assetTypes',
                    helpText: t('::Dashboard:Settings:PortfolioFilter:AssetType:HelpText'),
                    hidden:
                      valueType !== ValueType.TotalValue || valueSource !== ValueSource.Portfolio
                  }}
                />

                {/* Asset type */}
                {valueSource === ValueSource.Asset ? (
                  <AssetTypeSelect
                    assetTypes={validAssetTypes}
                    assetType={selectedAssetType}
                    onChange={setSelectedAssetType}
                  />
                ) : null}

                {/* Search asset */}
                <Form.Typeahead<ExternalAssetSearchDto, CalculatedValueDto>
                  name={'params.assetSearch'}
                  label={t('::Dashboard:Settings:EventView:AssetSearch')}
                  placeholder={t('::AssetSearch:Placeholder', {
                    0: t(`::Enum:AssetType:${selectedAssetType}`)
                  })}
                  keyExtractor={extractSymbol}
                  valueFormatter={asset => asset.name ?? ''}
                  isRequired
                  hidden={valueSource !== ValueSource.Asset}
                  search={search}
                  renderItemIcon={item =>
                    (item.value?.imageUri ?? item.value.market) ? (
                      <Image
                        rounded
                        size={'$1.5'}
                        source={
                          item.value?.imageUri ??
                          (item.value.market ? FLAGS[item.value.market as FlagKey] : undefined)
                        }
                      />
                    ) : (
                      <UnistocksCircle size={'$1.5'} />
                    )
                  }
                  onChange={setDisplayName}
                />

                {/* Date range */}
                <PeriodSelect
                  name={'params.period'}
                  hidden={!hasAnyFlag(valueType, periodDependentFlags)}
                  onChange={setDisplayName}
                />

                {/* Net savings type */}
                <Form.Select<NetSavingsType, CalculatedValueDto>
                  name={'params.netSavingsType'}
                  label={t('::Dashboard:Settings:EventView:NetSavingsType')}
                  isRequired
                  options={getNetSavingsTypeSelectOptions()}
                  hidden={valueType !== ValueType.NetSavings}
                  onChange={setDisplayName}
                />

                {/* Key financial ratio type */}
                <Form.Select<KeyFinancialRatio, CalculatedValueDto>
                  name={'params.keyFinancialRatio'}
                  label={t('::KeyFinancialRatio')}
                  isRequired
                  options={getKeyFinancialRatioSelectOptions()}
                  hidden={valueType !== ValueType.KeyFinancialRatio}
                  onChange={setDisplayName}
                />

                {/* Select currencies */}
                <Form.Typeahead<string, CalculatedValueDto>
                  name={'params.fromCurrency'}
                  label={t('::Dashboard:Settings:EventView:FromCurrency')}
                  placeholder={t('::DisplayName:Currency:Placeholder')}
                  filter={currencyTypeaheadFilter}
                  options={currencies}
                  hidden={valueSource !== ValueSource.ExchangeRate}
                  isRequired
                  valueFormatter={currencyValueFormatter}
                  valueTransform={currencyTransform}
                />

                <Form.Typeahead<string, CalculatedValueDto>
                  name={'params.toCurrency'}
                  label={t('::Dashboard:Settings:EventView:ToCurrency')}
                  placeholder={t('::DisplayName:Currency:Placeholder')}
                  helpText={
                    valueSource === ValueSource.Portfolio
                      ? t('::Dashboard:Settings:EventView:ToCurrency:HelpText')
                      : undefined
                  }
                  filter={currencyTypeaheadFilter}
                  options={currencies}
                  hidden={toCurrencyHidden(valueType, valueSource, portfolios)}
                  isRequired={valueSource === ValueSource.ExchangeRate}
                  valueFormatter={currencyValueFormatter}
                  valueTransform={currencyTransform}
                />

                {/* Display Name */}
                <Separator my={'$3'} />
                <Form.Input
                  name={'displayName'}
                  label={t('::Dashboard:Settings:EventView:Description')}
                  isRequired
                />
              </>
            );

            const renderNestedValuesGroup = () => (
              <>
                {/* Display Name */}
                <Form.Input
                  name={'displayName'}
                  label={t('::Dashboard:Settings:EventView:Description')}
                  isRequired
                />

                <Accordion type={'single'} defaultValue={'base'}>
                  <Accordion.Item value={'base'}>
                    <Accordion.Trigger>
                      {({ open }) => (
                        <Row justifyContent={'space-between'} alignItems={'center'}>
                          <H4 userSelect={'none'}>
                            {t('::Dashboard:Settings:EventView:RelativeValue:Base')}
                          </H4>
                          <Square transition={'quickLessBouncy'} rotate={open ? '180deg' : '0deg'}>
                            <ChevronDown size={'$2'} />
                          </Square>

                          <HelpText
                            ml={'auto'}
                            title={t('::Dashboard:Settings:EventView:Filter')}
                            text={t('::Dashboard:Settings:EventView:Filter:HelpText')}
                          />
                        </Row>
                      )}
                    </Accordion.Trigger>

                    <Accordion.Content>
                      <Separator mb={'$3'} />
                      <PortfolioFilter
                        portfolioSelectOverrides={{
                          name: 'nestedValues.0.params.portfolios',
                          helpText: t('::Dashboard:Settings:PortfolioFilter:Portfolio:HelpText')
                        }}
                        sectionSelectOverrides={{
                          name: 'nestedValues.0.params.sections',
                          helpText: t('::Dashboard:Settings:PortfolioFilter:Category:HelpText')
                        }}
                        assetTypeSelectOverrides={{
                          name: 'nestedValues.0.params.assetTypes'
                        }}
                      />
                    </Accordion.Content>
                  </Accordion.Item>

                  <Separator my={'$3'} />

                  <Accordion.Item value={'relative'}>
                    <Accordion.Trigger>
                      {({ open }: { open: boolean }) => (
                        <Row justifyContent={'space-between'} alignItems={'center'}>
                          <H4 userSelect={'none'}>
                            {t('::Dashboard:Settings:EventView:RelativeValue:Relative')}
                          </H4>
                          <Square transition={'quick'} rotate={open ? '180deg' : '0deg'}>
                            <ChevronDown size={'$2'} />
                          </Square>

                          <HelpText
                            ml={'auto'}
                            title={t('::Dashboard:Settings:EventView:Filter')}
                            text={t('::Dashboard:Settings:EventView:Filter:HelpText')}
                          />
                        </Row>
                      )}
                    </Accordion.Trigger>

                    <Accordion.Content>
                      <Separator mb={'$3'} />
                      <PortfolioFilter
                        portfolioSelectOverrides={{
                          name: 'nestedValues.1.params.portfolios',
                          helpText: t('::Dashboard:Settings:PortfolioFilter:Portfolio:HelpText')
                        }}
                        sectionSelectOverrides={{
                          name: 'nestedValues.1.params.sections',
                          helpText: t('::Dashboard:Settings:PortfolioFilter:Category:HelpText')
                        }}
                        assetTypeSelectOverrides={{
                          name: 'nestedValues.1.params.assetTypes'
                        }}
                      />
                    </Accordion.Content>
                  </Accordion.Item>
                  <Separator mt={'$3'} />
                </Accordion>
              </>
            );

            return (
              <>
                {valueType === ValueType.RelativeValue
                  ? renderNestedValuesGroup()
                  : renderValueFilterGroup()}
                <ExampleValueList />
              </>
            );
          }}
        </WatchValues>
      </MultiStepForm.Step>
    </MultiStepForm>
  );
};

/**
 * Build a single example result for the current value type selection.
 * Combines the per-type examples into one row so that multiple selected
 * value types render as multiple values in a single row using extraProperties.
 */

const ExampleValueList: React.FC = () => {
  const { displayName, valueSource, valueType, params } = useWatch<CalculatedValueDto>();

  return (
    <AnimatePresence>
      {valueType ? (
        <DashboardItemFrame
          key={'preview'}
          my={'$3'}
          transition={'quickLessBouncy'}
          enterStyle={{ o: 0, y: -20 }}
          exitStyle={{ o: 0, y: -20 }}
        >
          <Header>
            <Heading>{t('::Preview')}</Heading>
          </Header>

          <ValueListTable
            firstList={[
              getCombinedExampleValue(
                valueSource!,
                valueType,
                displayName!,
                params?.netSavingsType!
              )
            ]}
            renderRow={item => <ValueListRow calculatedValue={item} />}
          />
        </DashboardItemFrame>
      ) : null}
    </AnimatePresence>
  );
};

const toCurrencyHidden = (
  valueType: ValueType,
  valueSource: ValueSource | null,
  portfolios: string[] | null
) => {
  const hasMultiplePortfolios = portfolios && portfolios?.length > 1;
  const validType = [ValueType.TotalValue, ValueType.NominalChange, ValueType.NetSavings].includes(
    valueType
  );
  const validSource =
    valueSource && [ValueSource.ExchangeRate, ValueSource.Portfolio].includes(valueSource);

  return (
    !validType || !validSource || (!hasMultiplePortfolios && valueSource === ValueSource.Portfolio)
  );
};

const renderValueSourceIcon = (source: ValueSource) => {
  let Icon: React.ComponentType<IconProps>;

  switch (source) {
    case ValueSource.Portfolio:
      Icon = PieChart;
      break;

    case ValueSource.SharedPortfolio:
      Icon = Share2;
      break;

    case ValueSource.Asset:
      Icon = ChartLine;
      break;

    case ValueSource.ExchangeRate:
      Icon = DollarSign;
      break;

    default:
      return null;
  }

  return <HeaderIcon Icon={Icon} color={'$headingFont'} />;
};

const renderValueTypeIcon = (type: ValueType) => {
  let Icon: React.ComponentType<IconProps>;

  switch (type) {
    case ValueType.TotalValue:
      Icon = DollarSign;
      break;

    case ValueType.NominalChange:
      Icon = TrendingUp;
      break;

    case ValueType.PercentageChange:
      Icon = Percent;
      break;

    case ValueType.NetSavings:
      Icon = HandCoins;
      break;

    case ValueType.KeyFinancialRatio:
      Icon = Search;
      break;

    case ValueType.RelativeValue:
      Icon = Divide;
      break;

    default:
      return null;
  }

  return <HeaderIcon Icon={Icon} color={'$headingFont'} />;
};
